import React, { useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { MainLayout } from '@/components/layout/MainLayout'
import { Button } from '@/components/ui/button'
import { CampaignAthletesTable, CampaignPaymentsPanel } from '@/components/campaigns/DealTables'
import { DEAL_TYPE_LABELS, shortDate } from '@/lib/dealTypes'
import { CampaignDetailsCollapsible } from '@/components/campaign/CampaignDetailsCollapsible'
import { InviteAthleteModal } from '@/components/campaign/InviteAthleteModal'

import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { AlertCircle, ArrowLeft, UserPlus, Users } from 'lucide-react'
import { useGetCampaignDetailsQuery, useGetCollaborationsQuery } from '@/redux/marketerApi'

const creativeOptions = {
  voHook: {
    motivational: 'Motivational',
    funny: 'Funny',
    'daily-habit': 'Daily Habit',
    'behind-scenes': 'Behind-the-scenes',
  },
  introOverlay: {
    'sporty-grid': 'Sporty Grid',
    'high-energy': 'High Energy',
    minimalist: 'Minimalist',
    'clean-tech': 'Clean Tech',
  },
  midRollOverlay: {
    'offer-cta': 'Offer CTA',
    'feature-highlight': 'Feature Highlight',
    slogan: 'Slogan',
    lifestyle: 'Lifestyle Message',
  },
  footer: {
    'logo-only': 'Logo Only',
    'tagline-url': 'Tagline + URL',
    'cta-bar': 'CTA Bar',
    'social-handles': 'Social Handles',
  },
}

const CampaignDetails = () => {
  const { id = '' } = useParams()
  const [activeTab, setActiveTab] = React.useState('athletes')
  const [inviteModalOpen, setInviteModalOpen] = useState(false)

  const {
    data: campaign,
    isLoading: loading,
    error: queryError,
  } = useGetCampaignDetailsQuery(id, {
    skip: !id,
  })

  const { data: campaignCollaborations = [] } = useGetCollaborationsQuery(
    { campaignId: id },
    { skip: !id },
  )

  // Derive error string if needed (simple check)
  const error = queryError ? 'Failed to load campaign' : null

  if (loading) {
    return (
      <MainLayout>
        <div className="container mx-auto px-4 py-16 text-center text-muted-foreground">
          <p>Loading campaign...</p>
        </div>
      </MainLayout>
    )
  }

  if (!campaign) {
    return (
      <MainLayout>
        <div className="container mx-auto px-4 py-16 text-center">
          <h2 className="text-2xl font-bold text-foreground mb-4">Campaign not found</h2>
          <p className="text-muted-foreground mb-6">
            The campaign you're looking for doesn't exist or has been removed.
          </p>
          <Button asChild variant="lime">
            <Link to="/campaigns">Return to Campaigns</Link>
          </Button>
        </div>
      </MainLayout>
    )
  }

  const getCreativeName = (type: keyof typeof creativeOptions, id: string) => {
    return creativeOptions[type][id as keyof (typeof creativeOptions)[typeof type]] || id
  }

  return (
    <MainLayout>
      <div className="mx-auto max-w-[1440px] space-y-4 p-4 sm:p-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <Link
            to="/campaigns"
            className="inline-flex items-center gap-2 text-sm font-medium text-foreground hover:underline"
          >
            <ArrowLeft className="h-4 w-4" /> Back to campaigns
          </Link>
          <div className="flex gap-2">
            <Button
              variant="dark-outline"
              className="bg-card"
              onClick={() => setInviteModalOpen(true)}
            >
              <UserPlus className="h-4 w-4" /> Invite to Athios
            </Button>
            <Button asChild variant="lime">
              <Link to={`/campaign/${campaign.id}/athletes`}>
                <Users className="h-4 w-4" /> Shop athletes
              </Link>
            </Button>
          </div>
        </div>

        {/* The campaign at a glance, like the deal workspace */}
        <section className="flex flex-wrap items-center gap-5 rounded-2xl border border-border bg-card p-5 shadow-sm">
          {campaign.brand?.logo ? (
            <img
              src={campaign.brand.logo}
              alt=""
              className="h-16 w-16 shrink-0 rounded-xl object-cover"
            />
          ) : (
            <span
              className="flex h-16 w-16 shrink-0 items-center justify-center rounded-xl bg-[#0EA5E9] text-xl font-bold"
              style={{ color: '#fff' }}
            >
              {(campaign.brand?.name ?? campaign.name).slice(0, 2).toUpperCase()}
            </span>
          )}
          <div className="min-w-0 flex-1">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              {campaign.brand?.name ?? 'Campaign'}
            </p>
            <h1 className="text-3xl font-bold truncate text-foreground">{campaign.name}</h1>
            <p className="mt-1 flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
              {DEAL_TYPE_LABELS[campaign.dealType ?? 'content']}
              <span
                className={
                  campaign.status === 'active'
                    ? 'rounded-full bg-[#E8F9D2] px-2.5 py-0.5 text-xs font-semibold text-[#2F5E06]'
                    : 'rounded-full bg-[#F1F5F9] px-2.5 py-0.5 text-xs font-semibold capitalize text-[#475569]'
                }
              >
                {campaign.status === 'active' ? 'Active' : campaign.status}
              </span>
            </p>
          </div>
          <dl className="grid grid-cols-3 gap-6 text-sm">
            <div>
              <dt className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Dates
              </dt>
              <dd className="font-semibold text-foreground">
                {campaign.startDate
                  ? `${shortDate(campaign.startDate)} – ${shortDate(campaign.endDate) || '…'}`
                  : '—'}
              </dd>
            </div>
            <div>
              <dt className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Budget
              </dt>
              <dd className="font-semibold text-foreground">
                {campaign.budget ? `$${Math.round(campaign.budget).toLocaleString('en-US')}` : '—'}
              </dd>
            </div>
            <div>
              <dt className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Athletes
              </dt>
              <dd className="font-semibold text-foreground">{campaignCollaborations.length}</dd>
            </div>
          </dl>
        </section>

        {/* Tabs Section */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <div>
            <TabsList className="flex h-auto w-full flex-wrap gap-1 rounded-2xl border border-border bg-card p-1.5 shadow-sm">
              <TabsTrigger
                value="athletes"
                className="h-10 flex-1 rounded-xl px-4 text-sm font-semibold text-muted-foreground data-[state=active]:bg-[#C0FD72] data-[state=active]:text-[#0F172A] data-[state=active]:shadow-sm"
              >
                Athletes
              </TabsTrigger>
              <TabsTrigger
                value="payments"
                className="h-10 flex-1 rounded-xl px-4 text-sm font-semibold text-muted-foreground data-[state=active]:bg-[#C0FD72] data-[state=active]:text-[#0F172A] data-[state=active]:shadow-sm"
              >
                Payments
              </TabsTrigger>
              <TabsTrigger
                value="campaign-details"
                className="h-10 flex-1 rounded-xl px-4 text-sm font-semibold text-muted-foreground data-[state=active]:bg-[#C0FD72] data-[state=active]:text-[#0F172A] data-[state=active]:shadow-sm"
              >
                Campaign Details
              </TabsTrigger>
            </TabsList>
          </div>

          {/* Athletes Tab */}
          <TabsContent value="athletes" className="space-y-3 mt-6">
            {campaign.openToApplications && (
              <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-[#D9F99D] bg-[#F7FEE7] px-4 py-3">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-foreground">
                    Open to applications
                    <span className="ml-2 font-normal text-muted-foreground">
                      {DEAL_TYPE_LABELS[campaign.dealType ?? 'content']}
                      {campaign.dealType === 'gifting'
                        ? ` · product + bonuses up to $${((campaign as any).giftingTerms?.bonuses ?? []).reduce((t: number, b: any) => t + b.amount, 0).toLocaleString('en-US')} · ${(campaign as any).giftingTerms?.usageDays ?? 30} days paid usage`
                        : campaign.payout
                          ? ` · $${Math.round(campaign.payout).toLocaleString()} per athlete${campaign.dealType === 'licensing' ? ' a month' : ''}`
                          : ''}
                      {campaign.applicationDeadline
                        ? ` · closes ${shortDate(campaign.applicationDeadline)}`
                        : ''}
                    </span>
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Athletes who fit see this in their Marketplace and apply.
                  </p>
                </div>
                {campaign.applicationCount ? (
                  <Button asChild variant="lime" size="sm">
                    <Link to="/campaigns?tab=incoming">{campaign.applicationCount} to review</Link>
                  </Button>
                ) : (
                  <span className="text-xs text-muted-foreground">No applications yet</span>
                )}
              </div>
            )}
            {(() => {
              const brief = (campaign as any).campaignBrief
              const incomplete =
                !brief ||
                !brief.primaryMessage?.trim() ||
                !brief.creativeDirection?.trim() ||
                !brief.objectives?.length
              if (!incomplete) return null
              return (
                <button
                  type="button"
                  className="flex w-full items-center gap-3 rounded-2xl border border-[#FCD9A8] bg-[#FFF4E5] px-4 py-3 text-left transition-colors hover:bg-[#FFEFD9]"
                  onClick={() => setActiveTab('campaign-details')}
                >
                  <AlertCircle className="h-5 w-5 shrink-0 text-[#B45309]" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-[#92400E]">
                      Your campaign brief is missing key details
                    </p>
                    <p className="mt-0.5 text-xs text-[#B45309]">
                      Athletes will create better content knowing your key message, objectives, and
                      creative direction before they get started. Tap to edit.
                    </p>
                  </div>
                </button>
              )
            })()}
            {campaignCollaborations.length > 0 ? (
              <CampaignAthletesTable list={campaignCollaborations} />
            ) : (
              <div className="flex flex-col items-center rounded-2xl border border-border bg-card px-4 py-14 text-center shadow-sm">
                <span className="flex h-16 w-16 items-center justify-center rounded-2xl bg-[#ECFCCB] text-[#4D7C0F]">
                  <UserPlus className="h-7 w-7" />
                </span>
                <h3 className="mt-5 text-xl font-bold text-foreground">No athletes yet</h3>
                <p className="mt-2 max-w-md text-muted-foreground">
                  Shop athletes' content in the marketplace and book what fits, or invite athletes
                  you already work with to join Athios.
                </p>
                <div className="mt-6 flex flex-wrap justify-center gap-3">
                  <Link
                    to={`/campaign/${campaign.id}/athletes`}
                    className="inline-flex items-center gap-2 rounded-xl bg-[#C0FD72] px-5 py-2.5 text-sm font-semibold text-[#0F172A] hover:bg-[#B4F25F]"
                  >
                    <Users className="h-4 w-4" /> Shop athletes
                  </Link>
                  <button
                    onClick={() => setInviteModalOpen(true)}
                    className="inline-flex items-center gap-2 rounded-xl border border-border bg-card px-5 py-2.5 text-sm font-semibold text-foreground hover:bg-muted"
                  >
                    <UserPlus className="h-4 w-4" /> Invite to Athios
                  </button>
                </div>
              </div>
            )}
          </TabsContent>

          {/* Payments Tab */}
          <TabsContent value="payments" className="mt-6">
            <CampaignPaymentsPanel list={campaignCollaborations} budget={campaign.budget} />
          </TabsContent>

          {/* Campaign Details Tab */}
          <TabsContent value="campaign-details" className="mt-6">
            <CampaignDetailsCollapsible
              campaign={campaign as any}
              getCreativeName={getCreativeName}
            />
          </TabsContent>
        </Tabs>
      </div>

      <InviteAthleteModal
        open={inviteModalOpen}
        onOpenChange={setInviteModalOpen}
        campaignId={id}
      />
    </MainLayout>
  )
}

export default CampaignDetails
